import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Fish, Save, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { getAllSpecies, createSpecie, updateSpecie, deleteSpecie, initializeDB } from "@/services/localDatabase";

const Specie = () => {
  const [species, setSpecies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSpecie, setEditingSpecie] = useState(null);
  const [formData, setFormData] = useState({
    nome_comune: "",
    nome_scientifico: ""
  });

  useEffect(() => {
    fetchSpecies();
  }, []);

  const fetchSpecies = async () => {
    try {
      await initializeDB(); // Ensure DB is initialized with default species
      const data = await getAllSpecies();
      setSpecies(data);
    } catch (error) {
      console.error("Error fetching species:", error);
      toast.error("Errore nel caricamento delle specie");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (specie = null) => {
    if (specie) {
      setEditingSpecie(specie);
      setFormData({
        nome_comune: specie.nome_comune,
        nome_scientifico: specie.nome_scientifico || ""
      });
    } else {
      setEditingSpecie(null);
      setFormData({ nome_comune: "", nome_scientifico: "" });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSpecie(null);
    setFormData({ nome_comune: "", nome_scientifico: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome_comune.trim()) {
      toast.error("Il nome comune è obbligatorio");
      return;
    }

    try {
      if (editingSpecie) {
        await updateSpecie(editingSpecie.id, formData);
        toast.success("Specie aggiornata con successo");
      } else {
        await createSpecie(formData);
        toast.success("Specie aggiunta con successo");
      }
      fetchSpecies();
      handleCloseDialog();
    } catch (error) {
      console.error("Error saving specie:", error);
      toast.error("Errore nel salvataggio della specie");
    }
  };

  const handleDelete = async (specie) => {
    if (!window.confirm(`Sei sicuro di voler eliminare "${specie.nome_comune}"?`)) {
      return;
    }

    try {
      await deleteSpecie(specie.id);
      toast.success("Specie eliminata con successo");
      fetchSpecies();
    } catch (error) {
      console.error("Error deleting specie:", error);
      toast.error("Errore nell'eliminazione della specie");
    }
  };

  return (
    <div className="p-6 md:p-8 lg:p-12 fade-in" data-testid="specie-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-barlow tracking-tight">
            Archivio Specie
          </h1>
          <p className="text-slate-500 mt-1">Gestisci le specie di molluschi bivalvi</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-orange-500 hover:bg-orange-600"
          data-testid="add-specie-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Aggiungi Specie
        </Button>
      </div>

      {/* Table */}
      <Card className="border-slate-100">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg font-barlow">
            <Fish className="w-5 h-5 text-orange-500" />
            Elenco Specie ({species.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-slate-500 text-center py-8">Caricamento...</p>
          ) : species.length === 0 ? (
            <p className="text-slate-500 text-center py-8">Nessuna specie presente</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome Comune</TableHead>
                  <TableHead>Nome Scientifico</TableHead>
                  <TableHead className="w-[120px]">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {species.map((specie) => (
                  <TableRow key={specie.id} data-testid={`specie-row-${specie.id}`}>
                    <TableCell className="font-medium">{specie.nome_comune}</TableCell>
                    <TableCell className="text-slate-500 italic">
                      {specie.nome_scientifico || "-"}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(specie)}
                          data-testid={`edit-specie-${specie.id}`}
                        >
                          <Edit2 className="w-4 h-4 text-slate-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(specie)}
                          data-testid={`delete-specie-${specie.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingSpecie ? "Modifica Specie" : "Nuova Specie"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="nome_comune">Nome Comune *</Label>
                <Input
                  id="nome_comune"
                  value={formData.nome_comune}
                  onChange={(e) => setFormData(prev => ({ ...prev, nome_comune: e.target.value.toUpperCase() }))}
                  placeholder="es. VONGOLA VERACE"
                  className="mt-1"
                  data-testid="input-nome-comune"
                />
              </div>
              <div>
                <Label htmlFor="nome_scientifico">Nome Scientifico</Label>
                <Input
                  id="nome_scientifico"
                  value={formData.nome_scientifico}
                  onChange={(e) => setFormData(prev => ({ ...prev, nome_scientifico: e.target.value }))}
                  placeholder="es. Ruditapes philippinarum"
                  className="mt-1"
                  data-testid="input-nome-scientifico"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                <X className="w-4 h-4 mr-2" />
                Annulla
              </Button>
              <Button type="submit" className="bg-orange-500 hover:bg-orange-600" data-testid="save-specie-btn">
                <Save className="w-4 h-4 mr-2" />
                Salva
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Specie;
